import ContactAddress from "./contact-address";
import ContactExtraFields from "./contact-extra-fields";
import ContactGroup from "./contact-group";
import { ContactPeppolEndpoint } from "./contact-peppol-endpoint";
import { getModel } from "@/utils/models/model";
import { ignoredFields, priorityFields, model, namedFilters } from "@/utils/models/decorator-model";
import {
	field,
	nullable,
	print,
	hidden,
	filterValueInput,
	filterOnly,
	computed,
	defaultTextIfValueSettings,
	dependencies,
	onClick,
	canBeFilteredNummerically
} from "@/utils/models/decorator-field";
import { DossierConfigService } from "@/services/dossier-config-service";
import {printBoolean, printUser, printWithoutHtmlTags, sellPriceCategory} from "@/utils/pretty-print";
import { DossierService } from "@/services/dossier-service";
import { HumanFilter, HumanFilterOperation } from "@/utils/human-filter";
import { ContactAddressNamedFilter } from "@/utils/models/contact-address-named-filter";
import { NamedFilter } from "@/utils/models/named-filter";
import { TranslateResult } from "vue-i18n";
import { ContactAwsDocument } from "./contact-aws-document";
import { ContactPriceRule } from "./contact-price-rule";
import { LedgerAccount } from "../ledger-account";
import { i18n } from "@/setup/i18n-setup";
import { createUserTextIfValueSettings } from "@/utils/models/default-text-if-value-settings";
import { ISendMessageDialogContact, splitMails } from "@/utils/mail";
import { AllContactsService, UserConfigService } from "@/services";
import { AwsDocument } from "./aws-document";
import { getOpenAwsDocumentsFunc } from "@/utils/models/click-action-handlers";
import { ContactStats } from "./contact-stats";
import Currency from "./currency";
import { VatRegime } from "@/services/vat-service";

export enum ContactType {
	Customer = 1,
	Supplier = 2,
	Prospect = 3
}

@model("Contact")
@ignoredFields(["MainAddress.Contact", "FriendlyID"])
@priorityFields(["ComputedFriendlyID", ...getModel("ContactAddress").getPriorityFields().map((f) => "MainAddress." + f)])
@namedFilters(Contact.getContactNamedFilters())
export default class Contact{
	@field("number")
	@filterValueInput("Contact")
	@filterOnly()
	public ID:number = 0;

	@field("string")
	@print(printWithoutHtmlTags)
	public Info:string = "";

	@field("boolean")
	@print(printBoolean)
	public Active:boolean = true;

	@field("boolean")
	public Disabled:boolean = true;

	@field("string")
	@nullable()
	public Vat:string | null = null;

	@field("string")
	@nullable()
	public CommercialVat:string | null = null;

	@field("number")
	@print((value:number, highestParent:any, lowestParent:Contact)=>{
		if (lowestParent.GroupID == 2) {
			return i18n.t("common.vat-regimes-purchase." + value).toString();
		}
		return i18n.t("common.vat-regimes." + value).toString();
	})
	public VatRegime:number = 1;

	@field("number")
	public GroupID:number = 0;

	@field("number")
	public DefaultVatID:number|null = null;

	@field("number")
	public MainAddressID:number = 0;

	@field("number")
	public PaymentDays:number = 30;

	@field("date")
	@print((val:Date)=>val.toLocaleDateString("nl-be", { year: "numeric", month: "2-digit", day: "2-digit" }))
	public TimeCreated:Date = new Date();

	@field("date")
	@print((val:Date)=>val.toLocaleDateString("nl-be", { year: "numeric", month: "2-digit", day: "2-digit" }))
	public TimeChanged:Date = new Date();

	@field("number")
	@print(sellPriceCategory)
	@filterValueInput("SellPriceCategory")
	public SellPriceCategory:number = 1;

	@field("boolean")
	@print(printBoolean)
	public PaymentEndMonth:boolean = false;

	@field("has-one", "ContactGroup")
	@nullable()
	public Group:ContactGroup | null = new ContactGroup();

	@field("has-one", "ContactAddress")
	@nullable()
	public MainAddress:ContactAddress | null = new ContactAddress();

	@field("has-many", "ContactAddress")
	public Addresses:ContactAddress[] = [];

	@field("number")
	@hidden()
	public FriendlyID:number = 0;

	@field("string")
	public ComputedFriendlyID:string = "";

	@field("string")
	public Iban:string = "";

	@field("string")
	public Bic:string = "";

	@field("string")
	public Barcode:string | null = null;

	@field("number")
	public Discount:number = 0;

	@field("number")
	public DiscountCash:number = 0;

	@field("number")
	public DiscountCashDays:number = 8;

	@field("boolean")
	@print(printBoolean)
	public Blocked:boolean = false;

	@field("number")
	public MaxReminder:number = 5;

	@field("number")
	public CurrencyID:number = 1;
	@field("has-one", "Currency")
	public Currency:Currency | null = null;

	@field("number")
	public ExtraFieldsID:number = 0;

	@field("has-one", "ContactExtraFields")
	public ExtraFields:ContactExtraFields = new ContactExtraFields();

	@field("number")
	public DefaultJournalID:number = 1;

	@field("number")
	@filterValueInput("Creator")
	@print(printUser)
	@defaultTextIfValueSettings(createUserTextIfValueSettings("DefaultCreatorID"))
	public DefaultCreatorID:number = 0;

	@field("number")
	@print(printUser)
	@filterValueInput("Creator")
	@defaultTextIfValueSettings(createUserTextIfValueSettings("CreatorID"))
	public CreatorID:number = 0;

	@field("string")
	public MollieCustomerID:string = "";


	@field("string")
	@computed()
	@hidden()
	public get Creator():string {
		let user = DossierService.getUser(this.CreatorID);
		return user.getUserString();
	}

	@field("string")
	@computed()
	@onClick({
		getName(){
			return i18n.t(`models.friendlyNames.Contact.CallMe`).toString();
		},
		canBeUsed(){
			return true;
		},
		handler(target:Object, prop:PropertyKey){
			let parts = prop.toString().split(".");
			parts.pop();
			for (let part of parts) {
				target = (target as any)[part] as Object;
				if (!target) return;
			}
			console.log(target);
			let phone = (target as Contact).getCleanPhone();
			if (!phone) return;
			let el = document.createElement("a");
			el.href = phone;
			el.click();
			el.remove();
		},
		cannotBeDisabled: true
	})
	@dependencies(["MainAddress"])
	public get CallMe():string {
		if (!this.MainAddress) return "";
		if (!this.MainAddress.Phone) return "";
		return `<span style='color:#1c86f0'>` + i18n.t(`models.friendlyNames.Contact.CallMe`).toString() + `</span>`;
	//	let p = this.getCleanPhone();
	//	return this.MainAddress?.Phone ? "<a href='" + p + "' style='text-decoration: none; color:#1c86f0'>"+ i18n.t(`models.friendlyNames.Contact.CallMe`).toString() +"</a>" : "";
	}

	@field("number")
	@computed()
	@dependencies(["Addresses"])
	public get AddressCount():number{
		return this.Addresses.length;
	}

	@field("string")
	@canBeFilteredNummerically()
	public DefaultLedgerAccountCode:string = "";
	@field("has-one", "LedgerAccount")
	public DefaultLedgerAccount:LedgerAccount | null = null;

	public AWSDocuments:ContactAwsDocument[] = [];
	public PriceRules:ContactPriceRule[] = [];
	public UnprocessedFiles: File[] = [];
	public toDeleteFiles:ContactAwsDocument[] = [];

	@field("number")
	@filterValueInput("Contact")
	@filterOnly()
	@hidden()
	public ProspectBecameAClientWithID:number = 0;

	@field("number")
	@filterValueInput("Contact")
	@filterOnly()
	@hidden()
	public ClientCameFromProspectWithID:number = 0;

	@field("boolean")
	@print(printBoolean)
	@onClick(getOpenAwsDocumentsFunc(async function(value:any):Promise<AwsDocument[]>{
		let result = await AllContactsService.getContactByID(value.ID);
		return result.AWSDocuments;
	}))
	public HasAttachedDocument:boolean = false;

	@field("has-one", "ContactStats")
	public Stats:ContactStats | null = null;

	@field("has-many", "ContactPeppolEndpoint")
	public PeppolEndpoints:ContactPeppolEndpoint[] = [];
	@field("number")
	@hidden()
	public DefaultPeppolEndpointID:number = 0;
	@field("has-one", "ContactPeppolEndpoint")
	public DefaultPeppolEndpoint:ContactPeppolEndpoint | null = null;

	@field("number")
	@hidden()
	public DefaultDeliveryAddressID:number = 0;

	@field("has-one", "ContactAddress")
	public DefaultDeliveryAddress:ContactAddress | null = null;

	@field("number")
	public DefaultBankID:number = 0;

	@field("string")
	public CentralisationLedgerAccountCode:string = "";
	@field("has-one", "LedgerAccount")
	public CentralisationLedgerAccount:LedgerAccount | null = null;

	// eslint-disable-next-line max-lines-per-function
	public constructor(data?:any) {
		if (!data) {
			let contactConfig = DossierConfigService.getContactsConfig();
			let userConfig = UserConfigService.getGeneralSettings();
			this.PaymentDays = contactConfig.DefaultPaymentDays;
			this.DiscountCashDays = contactConfig.DefaultDiscountCashDays;
			this.Vat = userConfig.NewCustommersAreBuisinessesByDefault ? "" : null;
			this.SellPriceCategory = contactConfig.DefaultSellPriceCategoryForNewClients || 1;
			return;
		}
		this.ID = data.ID;
		this.Active = data.Active;
		this.Disabled = !data.Active;
		this.Info = data.Info;
		this.Vat = data.Vat;
		this.CommercialVat = data.CommercialVat;
		this.VatRegime = data.VatRegime;
		this.GroupID = data.GroupID;
		this.DefaultVatID = this.getDefaultVatID(data);
		this.MainAddressID = data.MainAddressID;
		this.PaymentDays = data.PaymentDays;
		this.PaymentEndMonth = data.PaymentEndMonth || false;
		this.TimeCreated =  new Date(data.TimeCreated);
		this.TimeChanged = new Date(data.TimeChanged);
		this.SellPriceCategory = data.SellPriceCategory;
		this.FriendlyID = data.FriendlyID;
		this.ComputedFriendlyID = data.ComputedFriendlyID;
		this.Iban = data.Iban;
		this.Bic = data.Bic;
		this.Barcode = data.Barcode || null;
		this.Discount = data.Discount;
		this.DiscountCash = data.DiscountCash;
		this.DiscountCashDays = data.DiscountCashDays || 0;
		this.Blocked = data.Blocked || false;
		this.MaxReminder = data.MaxReminder;
		this.CurrencyID = data.CurrencyID;
		if (data.Currency) {
			this.Currency = new Currency(data.Currency);
		}
		this.ExtraFieldsID = data.ExtraFieldsID;
		this.DefaultJournalID = data.DefaultJournalID;
		this.DefaultCreatorID = data.DefaultCreatorID;
		this.CreatorID = data.CreatorID;
		this.MollieCustomerID = data.MollieCustomerID;

		this.PeppolEndpoints = ((data.PeppolEndpoints || []) as any[]).map(e=>{
			let result = new ContactPeppolEndpoint(e);
			if (result.ID == data.DefaultPeppolEndpointID){
				result.IsDefault = true;
			}
			return result;
		});
		this.DefaultPeppolEndpointID = data.DefaultPeppolID;
		if (data.DefaultPeppolEndpoint){
			this.DefaultPeppolEndpoint = new ContactPeppolEndpoint(data.DefaultPeppolEndpoint);
		}
		if (this.PeppolEndpoints.length > 0){
			this.DefaultPeppolEndpoint = this.PeppolEndpoints.find(e=>e.ID == this.DefaultPeppolEndpointID) || null;
		}

		if (data.Group) {
			this.Group = new ContactGroup(data.Group);
		}

		if (data.MainAddress) {
			this.MainAddress = new ContactAddress(data.MainAddress);
		}

		if (data.Addresses) {
			this.Addresses = data.Addresses.map((a: any) => new ContactAddress(a));
			this.Addresses.forEach(a=>{
				if (a.IsMainAddress){
					this.MainAddress = a;
				}
			});
		}

		if (data.ExtraFields) {
			this.ExtraFields = new ContactExtraFields(data.ExtraFields);
			this.ExtraFields.contactGroupId = this.GroupID;
		}

		if (this.Addresses.indexOf(this.MainAddress as any) == -1){
			let mainIndex = this.Addresses.findIndex((a) => a.ID == this.MainAddressID);
			if (mainIndex != -1) {
				this.MainAddress = this.Addresses[mainIndex];
			} else if (this.MainAddress) {
				this.Addresses.push(this.MainAddress);
			}
		}

		if (data.AWSDocuments) {
			this.AWSDocuments = (data.AWSDocuments as any[]).map(d=>new ContactAwsDocument(d));
		}

		if (data.PriceRules) {
			this.PriceRules = (data.PriceRules as any[]).map((r:any)=>new ContactPriceRule(r)).sort((a,b)=>a.DisplayOrder-b.DisplayOrder);
		}

		this.DefaultLedgerAccountCode = data.DefaultLedgerAccountCode || "";
		if (data.DefaultLedgerAccount){
			this.DefaultLedgerAccount = new LedgerAccount(data.DefaultLedgerAccount);
		}

		if (data.Stats) {
			this.Stats = new ContactStats(data.Stats);
		}

		this.CentralisationLedgerAccountCode = data.CentralisationLedgerAccountCode;
		if (data.CentralisationLedgerAccount) {
			this.CentralisationLedgerAccount = new LedgerAccount(data.CentralisationLedgerAccount);
		}

		this.ProspectBecameAClientWithID = data.ProspectBecameAClientWithID || 0;
		this.ClientCameFromProspectWithID = data.ClientCameFromProspectWithID || 0;
		this.HasAttachedDocument = data.HasAttachedDocument;

		this.DefaultBankID = data.DefaultBankID || 0;

		this.DefaultDeliveryAddressID = data.DefaultDeliveryAddressID || 0;
		if (data.DefaultDeliveryAddressID) {
			this.DefaultDeliveryAddress = this.Addresses.find(a=>a.ID == this.DefaultDeliveryAddressID) || null;
			if (!this.DefaultDeliveryAddress && data.DefaultDeliveryAddress) {
				this.DefaultDeliveryAddress = new ContactAddress(data.DefaultDeliveryAddress);
			}
		}
	}


	// eslint-disable-next-line max-lines-per-function
	public getJSON(): any {
		return {
			ID: this.ID,
			Active: this.Active,
			Disabled: this.Disabled,
			Info: this.Info,
			CommercialVat: this.CommercialVat,
			Vat: this.Vat,
			VatRegime: this.VatRegime,
			GroupID: this.GroupID,
			DefaultVatID: this.DefaultVatID,
			TimeCreated: this.TimeCreated.toJSON(),
			TimeChanged: this.TimeChanged.toJSON(),
			MainAddressID: this.MainAddressID,
			PaymentDays: this.PaymentDays,
			PaymentEndMonth: this.PaymentEndMonth,
			SellPriceCategory: this.SellPriceCategory,
			FriendlyID: this.FriendlyID,
			ComputedFriendlyID: this.ComputedFriendlyID,
			Iban: this.Iban,
			Bic: this.Bic,
			Barcode: this.Barcode,
			Discount: this.Discount,
			DiscountCash: this.DiscountCash,
			DiscountCashDays: this.DiscountCashDays,
			Blocked: this.Blocked,
			MaxReminder: this.MaxReminder,
			CurrencyID: this.CurrencyID,
			MainAddress: this.MainAddress ? this.MainAddress.getJSON() : null,
			Addresses: this.Addresses.map(a => a.getJSON()),
			ExtraFieldsID: this.ExtraFieldsID,
			ExtraFields: this.ExtraFields ? this.ExtraFields.getJSON() : null,
			DefaultJournalID: this.DefaultJournalID,
			DefaultCreatorID: this.DefaultCreatorID,
			CreatorID: this.CreatorID,
			MollieCustomerID: this.MollieCustomerID,
			PriceRules: (this.PriceRules || []).map(r=>r.getJSON()).sort((a,b):number=>{
				return a.DisplayOrder - b.DisplayOrder;
			}),
			DefaultLedgerAccountCode: this.DefaultLedgerAccountCode || "",
			ProspectBecameAClientWithID: this.ProspectBecameAClientWithID,
			ClientCameFromProspectWithID: this.ClientCameFromProspectWithID,
			HasAttachedDocument: this.HasAttachedDocument,
			PeppolEndpoints: this.PeppolEndpoints.map(e => e.getJSON()),
			DefaultDeliveryAddressID: this.DefaultDeliveryAddressID,
			DefaultBankID: this.DefaultBankID,
			CentralisationLedgerAccountCode: this.CentralisationLedgerAccountCode
		};
	};


	public setMainAddress() {
		if (!this.MainAddressID) {
			return;
		}

		let v = this.Addresses.find((v) => v.ID == this.MainAddressID);
		if (v) {
			this.MainAddress = v;
		}
	};

	public setDefaultPeppolEndpoint(endpoint:ContactPeppolEndpoint) {
		for (let e of this.PeppolEndpoints) {
			e.IsDefault = false;
		}
		endpoint.IsDefault = true;
		this.DefaultPeppolEndpoint = endpoint;
	}

	public getDisplayName():string {
		if (this.MainAddress) {
			if (this.MainAddress.CompanyName){
				return this.MainAddress.CompanyName;
			}
			if (this.MainAddress.FullName){
				return this.MainAddress.FullName;
			}
		}
		return this.ComputedFriendlyID;
	}

	public getCleanPhone():string {
		if (this.MainAddress) {
			return "tel:" + this.MainAddress.Phone.replace(/\D/g,"");
		}
		return "";
	}

	public getCleanMobile():string {
		if (this.MainAddress) {
			return "tel:" + this.MainAddress.Mobile.replace(/\D/g,"");
		}
		return "";
	}

	public getDefaultVatID(data:any):number|null{

		if(data.DefaultVatID == undefined){
			return null;
		}

		if(data.VatRegime!=1){
			return 1;
		}

		return data.DefaultVatID || null;
	}

	public getPersonalName():string {
		if (this.MainAddress) {
			if (this.MainAddress.FirstName && this.MainAddress.CompanyName){
				return this.MainAddress.FirstName + ", " + this.MainAddress.CompanyName;
			}
			if (this.MainAddress.CompanyName){
				return this.MainAddress.CompanyName;
			}
		}
		return "";
	}

	private static getContactAddressNamedFilterFieldNames():string[] {
		return [
			"CompanyName",
			"LastName",
			"FirstName",
			"AddressLine1",
			"AddressLine2",
			"Postal",
			"Email",
			"Phone",
			"Mobile",
			"Fax",
			"City",
			"Lng",
			"CountryCode",
			"Reference",
			"CompanyOrFullName",
			"FullName",
			"FullNameReverse",
			"PostalAndCity",
		];
	}

	public static getContactNamedFilters():NamedFilter[] {
		let fields = Contact.getContactAddressNamedFilterFieldNames();
		let model = getModel("ContactAddress");
		let result:NamedFilter[] = fields.map(f=>{
			let namedFilter = new ContactAddressNamedFilter({
				name: `address-field-${f}`,
				filterToString(filter:HumanFilter):TranslateResult {
					let newFilter = new HumanFilter({
						Field: `this.${f}`,
						Operator: filter.Options[0] as HumanFilterOperation,
						Values: filter.Values, IsOr: filter.Options[1] == "true"
					});
					return i18n.t("models.friendlyNames.Contact.namedFilterHasAddressWithField", {field: newFilter.getTranslatedText(model)});
				},
				canBeUsedAsDefaultViewFilter: true
			});
			namedFilter.fieldName = f;
			return namedFilter;
		});
		result.unshift(new NamedFilter({
			name: "is-particulier",
			filterToString(filter:HumanFilter):TranslateResult {
				return "is-particulier";
			},
			canBeUsedAsDefaultViewFilter: false,
			componentName: "custom-checkbox-filter"
		}));
		return result;
	}

	public getVatCountryCodeAndNumberSeperated():{countryCode:string, number:string} {
		if (!this.Vat || this.Vat.length < 2) {
			return {countryCode: "", number: ""};
		}
		let countryCode = this.Vat.substring(0,2);
		if (countryCode.match(/^[a-zA-Z]{2}$/)){
			return {countryCode: this.Vat.substring(0,2), number: this.Vat.substring(2)};
		}
		return {countryCode: "", number: this.Vat};
	}

	public getSendMessageDialogContacts():ISendMessageDialogContact[]{
		let result:ISendMessageDialogContact[] = [];
		for (let address of this.Addresses) {
			let mails = splitMails(address.Email);
			result.push(...(mails.map(m=>{
				return {name: address.getDisplayedName(), value: m };
			})));
		}
		return result.filter(r=>r.value.trim() != "");
	}

	public autoFillBic(){
		if(!this.Iban || this.Bic != "")
			return;

		const countryCode = this.Iban.replace(/\s+/g, "").substring(0, 2);

		let bankCode = "";
		if (countryCode === "BE") {
			bankCode = this.Iban.replace(" ","").substring(4, 7);
		} else if (countryCode === "NL") {
			bankCode = this.Iban.replace(" ","").substring(4, 8);
		}

		const bicMapping = require(`@/assets/json/bic-mappings.json`) as any;
		const bicCode = bicMapping[bankCode];

		if (!bicCode) {
			return false;
		}

		this.Bic = bicCode;
	}

	public getDefaultEmailForRemindersOrSaleType(saleTypeId:number):string {
		if (this.MainAddress != null) {
			if (this.MainAddress.EmailSettings.DefaultForRemindersMailIndex != null) {
				return this.MainAddress.getEmailByIndex(this.MainAddress.EmailSettings.DefaultForRemindersMailIndex);
			}
		}
		for (let address of this.Addresses) {
			if (address.EmailSettings.DefaultForRemindersMailIndex != null) {
				return address.getEmailByIndex(address.EmailSettings.DefaultForRemindersMailIndex);
			}
		}
		return this.getDefaultEmailForRemindersOrSaleType(saleTypeId);
	}


	public getDefaultEmailForSaleTypeId(saleTypeId:number):string {
		if (this.MainAddress != null) {
			let result = this.MainAddress.getEmailForSaleTypeId(saleTypeId);
			if (result != "") {
				return result;
			}
		}
		for (let address of this.Addresses) {
			let result = address.getEmailForSaleTypeId(saleTypeId);
			if (result != "") {
				return result;
			}
		}
		return "";
	}

	public getDefaultEmail():string {
		if (this.MainAddress != null) {
			return this.MainAddress.getDefaultEmail();
		}
		for (let address of this.Addresses) {
			let result = address.getDefaultEmail();
			if (result != "") {
				return result;
			}
		}
		return "";
	}

	public getAdditionalBccEmails():string[] {
		let list:string[] = [];
		for (let address of this.Addresses){
			list.push(...address.Email.split(";"));
		}
		if (this.MainAddress){
			list.push(...this.MainAddress.Email.split(";"));
		}
		list = list.filter((v, i)=>{
			if (!v) return false;
			let foundI = list.indexOf(v);
			if (foundI != i) return false;
			return true;
		});
		return list;
	}


	public isVatValidForVatRegime():boolean {
		if (this.GroupID > 2) return true;
		let vatRegimes = [
			VatRegime.VERKOOP_AAN_ANDERE_LIDSTATEN_BINNEN_DE_EG,
			VatRegime.BTW_MEDECONTRACTANT,
			VatRegime.DIENSTEN_AAN_ANDERE_LIDSTATEN_BINNEN_DE_EG
		];
		if (vatRegimes.indexOf(this.VatRegime) != -1 && !this.Vat) {
			return false;
		}
		return true;
	}

	public getSearchPeppolEndpointString():string{
		return this.getPreferedPeppolEndpoint() || this.Vat || this.CommercialVat || "";
	}

	public getPreferedPeppolEndpoint():string{
		if (this.DefaultPeppolEndpoint) {
			let id = this.DefaultPeppolEndpoint.getFullID();
			if (id) {
				return id;
			}
		}
		for (let endpoint of this.PeppolEndpoints) {
			let id = endpoint.getFullID();
			if (id) {
				return id;
			}
		}
		return "";
	}

}